import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type {
  CampaignMediaItem,
  ManualCampaignForm,
  PreviewData,
} from '@/features/campaigns/types/campaign';
import { useManualAlertOptional } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { useUploadSingleFile } from '@/shared/lib/upload/useUploadSingleFile';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';
import {
  buildOrientationWarningCopy,
  normalizeOrientations,
} from './previewStepHelpers';
import type { AltCreativePreview } from './PreviewStep.types';

type UseAltCreativeUploadParams = {
  data: PreviewData;
  campaignData: ManualCampaignForm;
  altMediaIds: CampaignMediaItem[];
  setAltMediaIds: React.Dispatch<React.SetStateAction<CampaignMediaItem[]>>;
};

/** Manages uploading alternate-orientation creatives (or accepting AI conversion) when a campaign's screens need an orientation the launched creative doesn't cover. */
export function useAltCreativeUpload({
  data,
  campaignData,
  altMediaIds,
  setAltMediaIds,
}: UseAltCreativeUploadParams) {
  const alert = useManualAlertOptional();
  const required = useMemo(
    () => normalizeOrientations(data.requiredOrientation),
    [data.requiredOrientation],
  );

  const primaryOrientations = useMemo(() => {
    const set = new Set<string>();
    (campaignData.mediaIds || []).forEach((m) => {
      if (m.orientation) set.add(String(m.orientation).toUpperCase());
    });
    return set;
  }, [campaignData.mediaIds]);

  const targetsNeedingAlt = useMemo(() => {
    return required.filter((o) => !primaryOrientations.has(o));
  }, [required, primaryOrientations]);

  const needsBoth =
    required.includes('PORTRAIT') && required.includes('LANDSCAPE');

  const uploadTargets = useMemo(() => {
    return needsBoth
      ? (['LANDSCAPE', 'PORTRAIT'] as const).filter((o) =>
          targetsNeedingAlt.includes(o),
        )
      : targetsNeedingAlt;
  }, [needsBoth, targetsNeedingAlt]);

  const showOrientationWarning = uploadTargets.length > 0;

  const currentMediaOrientation = String(
    campaignData.mediaIds?.[0]?.orientation || '',
  ).toUpperCase();

  const requiredLabel = required.join(' & ');
  const warningCopy = useMemo(
    () => buildOrientationWarningCopy(required, currentMediaOrientation),
    [required, currentMediaOrientation],
  );

  const [altPreviewByOrientation, setAltPreviewByOrientation] = useState<
    Record<string, AltCreativePreview>
  >(() => {
    const init: Record<string, AltCreativePreview> = {};
    (altMediaIds || []).forEach((m) => {
      const ori = String(m.orientation || '').toUpperCase();
      if (ori && m.mediaUrl) {
        init[ori] = {
          url: m.mediaUrl,
          type: m.mediaType || 'image/jpeg',
          name: `${ori} Creative`,
        };
      }
    });
    return init;
  });
  const [uploadingOrientation, setUploadingOrientation] = useState<
    string | null
  >(null);
  const [acceptedAiConvert, setAcceptedAiConvert] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingOrientation = useRef<string>('');
  const upload = useUploadSingleFile();
  const deleteFile = useDeleteFile();

  const deleteAltMedia = (mediaId?: string) => {
    const id = mediaId?.trim();
    if (!id) return;
    void deleteFile.mutateAsync({ mediaId: id }).catch(() => { });
  };

  const openUpload = (orientation: string) => {
    pendingOrientation.current = orientation;
    fileInputRef.current?.click();
  };

  const handleAltUpload = async (file?: File) => {
    if (!file) return;
    const orientation = pendingOrientation.current;
    if (!orientation) return;

    if (file.type.startsWith('video/') && file.size > 100 * 1024 * 1024) {
      if (alert) {
        alert.error('File too large', 'Video size should be less than 100MB.');
      } else {
        toast.error('Video size should be less than 100MB');
      }
      return;
    }
    if (file.type.startsWith('image/') && file.size > 25 * 1024 * 1024) {
      if (alert) {
        alert.error('File too large', 'Image size should be less than 25MB.');
      } else {
        toast.error('Image size should be less than 25MB');
      }
      return;
    }

    const previous = altMediaIds.find(
      (m) => String(m.orientation).toUpperCase() === orientation,
    );
    deleteAltMedia(previous?.mediaId);

    setUploadingOrientation(orientation);

    try {
      const result = await upload.mutateAsync({ file });
      const mediaId = String(result?.data?.mediaId || '');
      if (!mediaId) throw new Error('Upload failed');
      const rawOrientation = result?.dimensions?.orientation;
      const resolvedOrientation = String(
        rawOrientation &&
          ['PORTRAIT', 'LANDSCAPE', 'SQUARE'].includes(rawOrientation.toUpperCase())
          ? rawOrientation
          : orientation
      ).toUpperCase();
      const localUrl = URL.createObjectURL(file);

      setAltPreviewByOrientation((prev) => ({
        ...prev,
        [orientation]: {
          url: localUrl,
          type: file.type,
          name: file.name,
          size: file.size,
        },
      }));
      setAltMediaIds((prev) => {
        const without = prev.filter(
          (m) => String(m.orientation).toUpperCase() !== orientation,
        );
        return [
          ...without,
          {
            mediaId,
            orientation: resolvedOrientation,
            mediaUrl: result?.data?.url || result?.data?.mediaUrl || localUrl,
            mediaType: file.type,
          },
        ];
      });
      if (alert) {
        alert.success(
          'Creative uploaded',
          `${orientation} content is ready for launch.`,
        );
      } else {
        toast.success(`${orientation} content uploaded`);
      }
    } catch {
      if (alert) {
        alert.error('Upload failed', 'Failed to upload alternate creative.');
      } else {
        toast.error('Failed to upload content');
      }
    } finally {
      setUploadingOrientation(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const dropUpload = (orientation: string, file?: File) => {
    pendingOrientation.current = orientation;
    void handleAltUpload(file);
  };

  const removeAlt = (orientation: string) => {
    const existing = altMediaIds.find(
      (m) => String(m.orientation).toUpperCase() === orientation,
    );
    deleteAltMedia(existing?.mediaId);
    setAltMediaIds((prev) =>
      prev.filter((m) => String(m.orientation).toUpperCase() !== orientation),
    );
    setAltPreviewByOrientation((prev) => {
      const next = { ...prev };
      delete next[orientation];
      return next;
    });
  };

  const allUploadsReady =
    uploadTargets.length > 0 &&
    uploadTargets.every((o) =>
      altMediaIds.some((m) => String(m.orientation).toUpperCase() === o),
    );
  const bannerResolved = allUploadsReady || acceptedAiConvert;

  return {
    requiredLabel,
    warningCopy,
    currentMediaOrientation,
    showOrientationWarning,
    uploadTargets,
    altPreviewByOrientation,
    uploadingOrientation,
    acceptedAiConvert,
    setAcceptedAiConvert,
    fileInputRef,
    openUpload,
    handleAltUpload,
    dropUpload,
    removeAlt,
    allUploadsReady,
    bannerResolved,
  };
}
